// Exact set of garbage characters identified by Search Backend (Jake Wright).
const GARBAGE_CHARS_RE = /[⌨↔✍█░▒▓□►◄◆●○★☜☞☨♣♥♦✓✤☐┃]/g

/**
 * Strips OCR garbage characters from transcript display text.
 * Removes Unicode symbol/block/arrow/dingbat characters that appear as
 * transcription artifacts (⌨ ↔ █ ▒ ✓ etc.) while preserving letters,
 * numbers, punctuation, and whitespace.
 *
 * @param {string} str - The transcript field value to clean
 * @returns {string} - Cleaned string suitable for display
 *
 * @example
 * stripGarbageCharacters("⌨word⌨")
 * // Returns: "word"
 */
export function stripGarbageCharacters(str) {
  if (!str) return ''
  return str.replaceAll(GARBAGE_CHARS_RE, '')
}

/**
 * Normalizes a string for comparison by removing diacritics, special characters,
 * converting to lowercase, and collapsing whitespace.
 *
 * Used for matching search keywords against fulltext transcripts where the backend
 * search service has already normalized the indexed content.
 *
 * @param {string} str - The string to normalize
 * @returns {string} - Normalized string suitable for comparison
 *
 * @example
 * normalizeForMatching("Francisco y Dolores ↔ María Getra")
 * // Returns: "francisco y dolores maria getra"
 */
export function normalizeForMatching(str) {
  if (!str) return ''

  // Step 1: Remove diacritics using Unicode NFD normalization
  // "María" → "Maria", "François" → "Francois"
  const withoutDiacritics = str.normalize('NFD').replaceAll(/[\u0300-\u036f]/g, '')

  // Step 2: Remove all non-alphanumeric characters except spaces
  // "Francisco ↔ Maria" → "Francisco  Maria"
  const alphanumericOnly = withoutDiacritics.replaceAll(/[^\p{L}\p{N}\s]/gu, '')

  // Step 3: Convert to lowercase
  const lowercase = alphanumericOnly.toLowerCase()

  // Step 4: Collapse multiple spaces and trim
  return lowercase.replaceAll(/\s+/g, ' ').trim()
}
